import { AppOrigin } from "@/domains/global/config";

export const STRIPE_DESKTOP_RETURN_ROUTE_PATH = "/subscription-return-desktop";

export const StripeDesktopReturnQueryParams = {
  DESKTOP_RETURN_PATH: "desktop_return_path",
} as const;

export const BrowserMetadataQueryParams = {
  SEGMENT_ANONYMOUS_ID: "segment_anonymous_id",
  GA_CLIENT_ID: "ga_client_id",
  GA_SESSION_ID: "ga_session_id",
} as const;

export interface BrowserMetadataQueryParamValues {
  segmentAnonymousId?: string | null;
  gaClientId?: string | null;
  gaSessionId?: string | null;
}

export function isValidDesktopReturnPath(returnPath: string | null): returnPath is string {
  if (!returnPath || !returnPath.startsWith("/") || returnPath.startsWith("//")) {
    return false;
  }

  try {
    const url = new URL(returnPath, AppOrigin.Production);
    return url.origin === AppOrigin.Production && url.pathname.startsWith("/");
  } catch {
    return false;
  }
}

export function buildDesktopStripeReturnDeepLink({
  browserMetadata,
  search,
}: {
  browserMetadata?: BrowserMetadataQueryParamValues;
  search: string;
}): URL | null {
  const params = new URLSearchParams(search);
  const desktopReturnPath = params.get(StripeDesktopReturnQueryParams.DESKTOP_RETURN_PATH);

  if (!isValidDesktopReturnPath(desktopReturnPath)) {
    return null;
  }

  const targetUrl = new URL(desktopReturnPath, AppOrigin.Production);
  params.delete(StripeDesktopReturnQueryParams.DESKTOP_RETURN_PATH);
  params.forEach((value, key) => {
    targetUrl.searchParams.set(key, value);
  });

  if (browserMetadata?.segmentAnonymousId) {
    targetUrl.searchParams.set(
      BrowserMetadataQueryParams.SEGMENT_ANONYMOUS_ID,
      browserMetadata.segmentAnonymousId
    );
  }

  if (browserMetadata?.gaClientId) {
    targetUrl.searchParams.set(BrowserMetadataQueryParams.GA_CLIENT_ID, browserMetadata.gaClientId);
  }

  if (browserMetadata?.gaSessionId) {
    targetUrl.searchParams.set(
      BrowserMetadataQueryParams.GA_SESSION_ID,
      browserMetadata.gaSessionId
    );
  }

  const targetPath = `${targetUrl.pathname}${targetUrl.search}`;
  const pathWithoutLeadingSlash = targetPath.startsWith("/") ? targetPath.slice(1) : targetPath;

  return new URL(pathWithoutLeadingSlash ? `mem://${pathWithoutLeadingSlash}` : "mem:///");
}
